import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { t } from 'i18n';
import 'regenerator-runtime/runtime';

import formatUnixTime from '../../formatUnixTime';
import transformData from '../../transformData';

import GraphErrorMessage from '../../GraphErrorMessage';
import selectAndTransformChartData from './selectAndTransformChartData';
import isChartDataEmpty from './isChartDataEmpty';

import {
  ResponsiveContainer,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis,
  Label,
  ScatterChart,
  Scatter,
  Cell,
} from 'recharts';

import {
  CARTESIAN_GRID_COLOR,
  CHART_DOMAIN,
  CHART_TICKS,
  CHART_HEIGHT,
  CHART_LABEL_INNER_HEIGHT,
  Y_AXIS_TICK_COUNT,
  Y_AXIS_DOMAIN,
  TARGET_RANGE_LOWER_BOUND,
  TARGET_RANGE_UPPER_BOUND,
  REFERENCE_LINE_COLOR,
  comparisonChartStyles,
} from '../../constants';

const i18nOpts = { scope: 'dashboard.chart' };

const ScatterPlot = ({ chartData, chartError, rangeEndDate, weekOrder, renderedDays }) => {
  const chartDataForTimeRange = useMemo(
    () => selectAndTransformChartData(chartData, rangeEndDate, transformData),
    [chartData, rangeEndDate, transformData]
  );
  const isChartEmpty = isChartDataEmpty(chartDataForTimeRange, weekOrder, renderedDays)
  const comparisonChartError = isChartEmpty && !chartError ? 'EMPTY_COMPARISON_CHART_DATA' : chartError;

  return (
    <div className="text-normal">
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ScatterChart width="100%" height="100%" margin={{ left: -12, bottom: 12, top: 8 }}>
          <CartesianGrid stroke={CARTESIAN_GRID_COLOR} />
          <XAxis
            dataKey="time"
            name="Time"
            tickFormatter={formatUnixTime}
            type="number"
            height={CHART_LABEL_INNER_HEIGHT}
            domain={CHART_DOMAIN}
            ticks={CHART_TICKS}
          >
            <Label position="insideBottom" offset={0}>
              {t('time_of_day', i18nOpts)}
            </Label>
          </XAxis>
          <YAxis dataKey="glucoseValue" tickCount={Y_AXIS_TICK_COUNT} domain={Y_AXIS_DOMAIN} type="number">
            <Label angle={270} position="insideLeft" style={{ textAnchor: 'middle' }} offset={18}>
              {t('glucose', i18nOpts)}
            </Label>
          </YAxis>

          {/* Do not update ReferenceArea and ReferenceLine into a single fragment
            until recharts library has been updated to a React Fragment supporting version */}
          {!isChartEmpty && (
            <ReferenceArea
              y1={TARGET_RANGE_LOWER_BOUND}
              y2={TARGET_RANGE_UPPER_BOUND}
              fill={REFERENCE_LINE_COLOR}
              fillOpacity={0.1}
            />
          )}
          {!isChartEmpty && <ReferenceLine y={TARGET_RANGE_UPPER_BOUND} stroke={REFERENCE_LINE_COLOR} />}
          {!isChartEmpty && <ReferenceLine y={TARGET_RANGE_LOWER_BOUND} stroke={REFERENCE_LINE_COLOR} />}

          {weekOrder.map((day, index) => {
            const isRendered = renderedDays[day];
            const dataForDayOfWeek = chartDataForTimeRange[index];

            if (!isRendered || !dataForDayOfWeek) return null;

            const fillColor = comparisonChartStyles[day].color;

            return (
              <Scatter name="glucoseChart" data={dataForDayOfWeek} isAnimationActive={false}>
                {dataForDayOfWeek.map((_, cellIndex) => (
                  <Cell key={`cell-${day}-${cellIndex}`} fill={fillColor} />
                ))}
              </Scatter>
            );
          })}
        </ScatterChart>
      </ResponsiveContainer>
      {comparisonChartError && <GraphErrorMessage status={comparisonChartError} />}
    </div>
  );
};

const mapStateToProps = ({
  dashboardComparisonChart: { rangeEndDate, renderedDays, weekOrder, chartData, chartError },
}) => {
  return {
    rangeEndDate,
    renderedDays,
    weekOrder,
    chartData,
    chartError,
  };
};

export default connect(mapStateToProps, null)(ScatterPlot);
